// ── Leaflet 마커 아이콘 생성 함수 ─────────────────────────────────────────────
// groupByOverlap: 픽셀 좌표 기반 겹침 클러스터 탐지
// createMeetingMarkerIcon: 단일 미팅 말풍선 마커
// createGroupMarkerIcon: 겹침 그룹 카드 마커
// 2026-10-24: 글자·색은 마커 표시 설정을 따른다(markerConfig.js — 지도와 설정 미리보기가 같은 함수를 쓴다).
//   config 를 안 주면 처음 값(상호 5글자 + 미팅시간 + 카테고리 앞글자, 색은 소속).

import L from 'leaflet';
import { MEETING_MARKER_ICON_CLASS, escapeHtml } from './utils';
import { buildMarkerLabel, markerColors } from './markerConfig';

// 두 마커 중심 픽셀 거리가 이 값 이하면 겹친 것으로 판단
// 마커 버블 높이(28px) + 여유(7px) = 35px 기준
export const OVERLAP_RADIUS = 35;

// 픽셀 좌표 기반 마커 그룹화 — 클러스터 중심 반경 기준 (그리디)
// 겹치는 마커만 감지해 오프셋+연결선으로 처리. 합치지 않음.
export function groupByOverlap(pts) {
  const clusters = []; // [{ cx, cy, members: [] }]

  for (const pt of pts) {
    // 현재 점과 가장 가까운 클러스터 찾기 (중심 기준)
    let nearest = null;
    let nearestDist = Infinity;
    for (const cl of clusters) {
      const dx = pt.px - cl.cx;
      const dy = pt.py - cl.cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = cl;
      }
    }

    if (nearest && nearestDist < OVERLAP_RADIUS) {
      // 기존 클러스터에 합류 후 중심 재계산
      nearest.members.push(pt);
      nearest.cx = nearest.members.reduce((s, p) => s + p.px, 0) / nearest.members.length;
      nearest.cy = nearest.members.reduce((s, p) => s + p.py, 0) / nearest.members.length;
    } else {
      // 새 클러스터 생성
      clusters.push({ cx: pt.px, cy: pt.py, members: [pt] });
    }
  }

  return clusters.map((cl) => cl.members);
}

// 단일/그룹 마커 아이콘 생성
// 그룹(2개 이상)이면 카드형 다중 행, 단일이면 기존 아이콘 사용
// extCategoryNames: 예전 인자(쓰지 않음 — 호출부 호환용으로 자리만 둔다)
export function createGroupMarkerIcon(groupPts, selectedId, extCategoryNames = [], config = null) {
  if (groupPts.length === 1) {
    return createMeetingMarkerIcon(
      groupPts[0].item,
      Number(selectedId) === Number(groupPts[0].item.id),
      extCategoryNames,
      config,
    );
  }

  const ROW_H = 22; // 행 높이(px) 추정
  const rows = groupPts.map(({ item }, idx) => {
    const label = escapeHtml(buildMarkerLabel(item, config));
    const c = markerColors(item, config);
    const isSel = Number(selectedId) === Number(item.id);
    const borderTop = idx > 0 ? 'border-top:1px solid rgba(255,255,255,0.34);' : '';
    return `<div data-meeting-id="${item.id}" style="padding:5px 10px;font-size:12px;font-weight:900;line-height:1.25;white-space:nowrap;background:${isSel ? c.selBg : c.bg};color:${c.fg};cursor:pointer;text-shadow:${c.textShadow};${borderTop}">${label}</div>`;
  }).join('');

  // 아이콘 크기: 가장 긴 행 기준 너비, 행 수 기준 높이
  const maxW = Math.max(...groupPts.map(({ item }) =>
    Math.min(Math.max(buildMarkerLabel(item, config).length * 8 + 24, 80), 210)
  ));
  const totalH = groupPts.length * ROW_H + 11; // 행 합계 + 화살표(9) + 여유

  // 겹친 마커 묶음도 테두리 없이 그림자만 (2026-09-23). 꼬리는 맨 아래 행 색을 이어받는다.
  const tailBg = markerColors(groupPts[groupPts.length - 1].item, config).bg;

  const html = `<div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-6px);pointer-events:none;filter:drop-shadow(0 5px 12px rgba(0,0,0,0.45));">
    <div style="display:flex;flex-direction:column;border-radius:12px;overflow:hidden;box-shadow:0 4px 10px rgba(0,0,0,0.40);pointer-events:auto;">${rows}</div>
    <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:8px solid ${tailBg};margin-top:-1px;"></div>
  </div>`;

  return L.divIcon({
    className: MEETING_MARKER_ICON_CLASS,
    html,
    iconSize:   [maxW, totalH],
    iconAnchor: [maxW / 2, totalH],
  });
}

/** 단일 마커 말풍선 HTML — 지도 아이콘과 설정 미리보기가 같이 쓴다. */
export function meetingMarkerHtml(item, selected, config = null) {
  const label = escapeHtml(buildMarkerLabel(item, config));
  const c = markerColors(item, config);
  // 테두리를 없애고 그림자만 남긴다 (2026-09-23 사용자 요청).
  //   흰 테두리는 반투명이라 화면을 어둡게 만드는 기기에서 회색으로 보였다. 지도 타일은 늘 밝으므로
  //   테두리 없이도 마커가 잘 보이고, 어느 기기에서나 같은 모양이 된다.
  //   고른 마커만 파란 링으로 표시한다(이것도 그림자라 테두리처럼 색이 변조되지 않는다).
  const bubbleShadow = selected
    ? '0 0 0 3px rgba(96,165,250,0.95),0 6px 14px rgba(0,0,0,0.45)'
    : '0 4px 10px rgba(0,0,0,0.40)';

  return `<div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-6px);pointer-events:none;filter:drop-shadow(0 4px 9px rgba(0,0,0,0.38));">
    <div style="padding:6px 11px;border-radius:999px;font-size:12px;font-weight:900;line-height:1.05;white-space:nowrap;background:${c.bg};color:${c.fg};box-shadow:${bubbleShadow};text-shadow:${c.textShadow};">${label}</div>
    <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:8px solid ${c.bg};margin-top:-1px;"></div>
  </div>`;
}

// 단일 미팅 마커 말풍선 아이콘 생성
// extCategoryNames: 예전 인자(쓰지 않음 — 호출부 호환용으로 자리만 둔다)
export function createMeetingMarkerIcon(item, selected, extCategoryNames = [], config = null) {
  return L.divIcon({
    className:  MEETING_MARKER_ICON_CLASS,
    html:       meetingMarkerHtml(item, selected, config),
    iconSize:   [120, 28],
    iconAnchor: [60, 28],
  });
}
